import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Badge, Input, Popover, RemovableBadge, StatusDot } from '@wit-support/ui';
import { cn } from './lib/cn.js';
import { isRawCampaignId, looksLikeUrl, normalizeCampaignIdInput, parseCampaignInput, } from './lib/campaign-input.js';
export { CAMPAIGN_RAW_ID_MIN_DIGITS, isRawCampaignId, looksLikeUrl, normalizeCampaignIdInput, parseCampaignInput, } from './lib/campaign-input.js';
const STATUS_RU = {
    ACTIVE: { label: 'Активна', dot: 'green' },
    PAUSED: { label: 'На паузе', dot: 'amber' },
    CAMPAIGN_PAUSED: { label: 'На паузе', dot: 'amber' },
    ADSET_PAUSED: { label: 'Группы на паузе', dot: 'amber' },
    IN_PROCESS: { label: 'Обрабатывается', dot: 'blue' },
    PENDING_REVIEW: { label: 'На проверке', dot: 'blue' },
    WITH_ISSUES: { label: 'С ошибками', dot: 'red' },
    DISAPPROVED: { label: 'Отклонена', dot: 'red' },
    ARCHIVED: { label: 'В архиве', dot: 'slate' },
    DELETED: { label: 'Удалена', dot: 'slate' },
};
/** Подпись и цвет статуса кампании Meta; незнакомый — как есть, серым. */
export function campaignStatusRu(status) {
    if (status === null || status === '')
        return { label: 'Статус неизвестен', dot: 'slate' };
    return STATUS_RU[status] ?? { label: status, dot: 'slate' };
}
const NO_NAME = 'Без названия';
/** Строка кампании: кабинет · название (обрезается) · id мелко · точка статуса. */
export function CampaignOptionRow({ option, showAccount = true, note, className, }) {
    const st = campaignStatusRu(option.status);
    const name = option.name ?? NO_NAME;
    return (_jsxs("span", { className: cn('flex min-w-0 flex-1 items-center gap-2', className), title: `${name} · ${option.campaignId}`, children: [showAccount && option.accountName ? (_jsx(Badge, { variant: "outline", className: "max-w-[8rem] shrink-0", children: _jsx("span", { className: "truncate", children: option.accountName }) })) : null, _jsx("span", { className: cn('min-w-0 flex-1 truncate', option.name === null && 'italic text-[var(--color-text-muted)]'), children: name }), note ? (_jsx("span", { className: "shrink-0 text-xs text-[var(--color-state-warning-fg)]", children: note })) : null, _jsx("span", { className: "shrink-0 font-mono text-xs tabular-nums text-[var(--color-text-muted)]", children: option.campaignId }), _jsx(StatusDot, { variant: st.dot, role: "img", "aria-live": "off", "aria-label": st.label, title: st.label })] }));
}
const rawOption = (campaignId) => ({
    campaignId,
    name: null,
    accountName: null,
    platformAccountId: null,
    status: null,
});
const DEFAULT_RAW_HINT = 'не найдена в нашей базе';
/** Ссылка вставлена, а id кампании в ней нет. */
export const CAMPAIGN_URL_NO_ID_TEXT = 'В ссылке нет id кампании — откройте кампанию в Ads Manager и скопируйте ссылку заново';
/** «кампания из кабинета X» — кампания вне кабинетов поиска; иначе undefined. */
function foreignNote(o, scope) {
    if (!scope || scope.length === 0 || o.platformAccountId === null)
        return undefined;
    if (scope.includes(o.platformAccountId))
        return undefined;
    return `кампания из кабинета ${o.accountName ?? o.platformAccountId}`;
}
/**
 * Выбор кампании Meta: выпадающий список, который фильтруется по мере ввода
 * (поиск делает родитель — `onQueryChange`), одиночный (`value: string |
 * null`) или множественный (`multiple`, `value: string[]`, выбранные —
 * чипами). Каждая строка — кабинет, название, id мелко, статус точкой.
 *
 * Клавиатура: ↑/↓ — по списку, Enter — выбрать, Esc — закрыть, Backspace в
 * пустом поле — снять последнее выбранное. Вставка id или ссылки Ads Manager
 * (`selected_campaign_ids=…`) превращается в id: найден в базе — обычный
 * выбор, нет — пункт «id … — не найдена в нашей базе» (`allowRawId`).
 */
export function CampaignPicker(props) {
    const { options, selectedOptions = [], knownOptions = [], platformAccountIds: scope, query, onQueryChange, loading = false, resolving = false, error = null, allowRawId = true, rawIdHint = DEFAULT_RAW_HINT, showAccount = true, onOpenChange, placeholder = 'Название или id кампании…', emptyLabel = 'Кампании не найдены', disabled = false, id, 'aria-label': ariaLabel = 'Кампания', 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, className, } = props;
    const values = props.multiple
        ? props.value
        : props.value !== null && props.value !== ''
            ? [props.value]
            : [];
    const max = props.multiple ? (props.max ?? Infinity) : 1;
    const [open, setOpenState] = React.useState(false);
    const [highlight, setHighlight] = React.useState(0);
    const fieldRef = React.useRef(null);
    const baseId = React.useId();
    const listboxId = `${baseId}-listbox`;
    const hintId = `${baseId}-hint`;
    const optionId = (i) => `${baseId}-option-${i}`;
    // уведомление — из обработчика, не из функции-обновления состояния (та
    // выполняется при отрисовке, а родителю нельзя менять состояние посреди неё)
    const setOpen = (next) => {
        if (next !== open)
            onOpenChange?.(next);
        setOpenState(next);
    };
    /** Всё, что знаем о кампаниях: подписи выбранных + текущая выдача. */
    const known = React.useMemo(() => {
        const m = new Map();
        for (const o of selectedOptions)
            m.set(o.campaignId, o);
        for (const o of knownOptions)
            if (!m.has(o.campaignId))
                m.set(o.campaignId, o);
        for (const o of options)
            if (!m.has(o.campaignId))
                m.set(o.campaignId, o);
        return m;
    }, [options, selectedOptions, knownOptions]);
    const resolved = React.useMemo(() => new Set([...selectedOptions, ...knownOptions].map((o) => o.campaignId)), [selectedOptions, knownOptions]);
    const optionOf = (cid) => known.get(cid) ?? rawOption(cid);
    const selected = React.useMemo(() => new Set(values), [values]);
    const trimmed = query.trim();
    /** Вставлена ссылка, а id кампании в ней нет: искать по тексту ссылки бессмысленно. */
    const urlWithoutId = looksLikeUrl(trimmed) && parseCampaignInput(trimmed).length === 0;
    const items = React.useMemo(() => {
        if (urlWithoutId)
            return [];
        const list = options
            .filter((o) => !(props.multiple && selected.has(o.campaignId)))
            .map((o) => ({ kind: 'option', option: o, note: foreignNote(o, scope) }));
        // целый id не из выдачи: известен вне фильтра — пункт с пометкой; нет —
        // «Взять id», но только по ответу на ЭТОТ ввод (пока ищем, может найтись)
        if (!loading && isRawCampaignId(trimmed)) {
            const listed = options.some((o) => o.campaignId === trimmed);
            const taken = props.multiple && selected.has(trimmed);
            const outside = knownOptions.find((o) => o.campaignId === trimmed);
            if (!listed && !taken && outside) {
                list.push({ kind: 'option', option: outside, note: foreignNote(outside, scope) });
            }
            else if (!listed && !taken && allowRawId) {
                list.push({ kind: 'raw', option: rawOption(trimmed) });
            }
        }
        return list;
    }, [
        urlWithoutId,
        options,
        knownOptions,
        scope,
        selected,
        props.multiple,
        allowRawId,
        loading,
        trimmed,
    ]);
    const itemsKey = items.map((i) => `${i.kind}:${i.option.campaignId}`).join('|');
    React.useEffect(() => {
        setHighlight(0);
    }, [query, itemsKey]);
    const atMax = props.multiple && values.length >= max;
    const isOpen = open && !disabled && !atMax;
    // «Загрузка…» — только пока нечего показать; со старой выдачей список живой
    const showLoading = loading && items.length === 0;
    // сбой поиска не мешает взять вставленный id (пункт «Взять id» не зависит от ответа)
    const navigable = isOpen && !showLoading && items.length > 0;
    const active = navigable ? Math.min(highlight, items.length - 1) : -1;
    // ↑/↓ уводят подсветку за край — список доезжает до неё
    React.useEffect(() => {
        if (active < 0)
            return;
        document.getElementById(`${baseId}-option-${active}`)?.scrollIntoView?.({ block: 'nearest' });
    }, [active, baseId]);
    function emit(nextIds) {
        if (props.multiple)
            props.onChange(nextIds, nextIds.map(optionOf));
        else {
            const cid = nextIds[0] ?? null;
            props.onChange(cid, cid === null ? null : optionOf(cid));
        }
    }
    function pick(item) {
        const cid = item.option.campaignId;
        if (props.multiple) {
            if (!selected.has(cid) && values.length < max)
                emit([...values, cid]);
        }
        else {
            emit([cid]);
            setOpen(false);
        }
        onQueryChange('');
        setHighlight(0);
    }
    function remove(cid) {
        emit(values.filter((v) => v !== cid));
    }
    function handleInput(raw) {
        // «1202 1000 …» — id с пробелами: пробелы убираем
        const text = normalizeCampaignIdInput(raw);
        // ссылка Ads Manager: id вместо ссылки (поиск по id находит её в базе);
        // id в ссылке нет — текст остаётся, список объясняет, что делать
        if (looksLikeUrl(text)) {
            const ids = parseCampaignInput(text);
            if (ids.length > 1 && props.multiple) {
                const next = [...values];
                for (const cid of ids)
                    if (!next.includes(cid) && next.length < max)
                        next.push(cid);
                emit(next);
                onQueryChange('');
                setOpen(true);
                return;
            }
            if (ids.length >= 1) {
                onQueryChange(ids[0]);
                setOpen(true);
                return;
            }
        }
        onQueryChange(text);
        setOpen(true);
    }
    function handleKeyDown(e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) {
                setOpen(true);
                return;
            }
            if (!navigable)
                return;
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setHighlight((h) => (Math.min(h, items.length - 1) + step + items.length) % items.length);
            return;
        }
        if (e.key === 'Enter') {
            // гасим Enter только когда есть что выбрать — поле живёт внутри формы
            if (active < 0)
                return;
            e.preventDefault();
            pick(items[active]);
            return;
        }
        if (e.key === 'Escape') {
            if (isOpen) {
                // закрыть список, но не диалог вокруг
                e.stopPropagation();
                setOpen(false);
            }
            return;
        }
        if (e.key === 'Backspace' && query === '' && values.length > 0) {
            e.preventDefault();
            remove(values[values.length - 1]);
        }
    }
    const unresolved = resolving ? [] : values.filter((v) => !resolved.has(v) && !known.has(v));
    const foreignSelected = values
        .map((v) => known.get(v))
        .filter((o) => o !== undefined)
        .map((o) => ({ o, note: foreignNote(o, scope) }))
        .filter((x) => x.note !== undefined);
    const describedBy = [ariaDescribedBy, unresolved.length > 0 ? hintId : null].filter(Boolean).join(' ') || undefined;
    return (_jsxs("div", { className: cn('flex flex-col gap-1', className), children: [_jsxs("div", { ref: fieldRef, className: cn('flex flex-wrap items-center gap-1.5 rounded-[var(--radius-2)] border border-[var(--color-border-default)] bg-[var(--color-surface-canvas)] px-2 py-1.5', 'focus-within:ring-2 focus-within:ring-[var(--color-border-focus)]', ariaInvalid && 'border-[var(--color-state-danger-fg)]', disabled && 'opacity-50'), children: [values.map((cid) => {
                        const o = optionOf(cid);
                        const label = o.name ?? cid;
                        return (_jsx(RemovableBadge, { size: "md", className: "min-w-0 max-w-full", removeAriaLabel: `Убрать кампанию ${label}`, onRemove: () => remove(cid), disabled: disabled, children: _jsxs("span", { className: "flex min-w-0 items-center gap-1.5", title: `${label} · ${cid}`, children: [showAccount && o.accountName ? (_jsxs("span", { className: "max-w-[7rem] shrink-0 truncate text-xs text-[var(--color-text-muted)]", children: [o.accountName, " \u00B7"] })) : null, _jsx("span", { className: "min-w-0 truncate", "data-testid": "campaign-chip-name", children: o.name ?? (resolving ? '…' : `id ${cid}`) }), o.name !== null ? (_jsx("span", { className: "shrink-0 font-mono text-xs text-[var(--color-text-muted)]", children: cid })) : null] }) }, cid));
                    }), _jsxs(Popover, { open: isOpen, onOpenChange: setOpen, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsx("div", { className: "min-w-40 flex-1", children: _jsx(Input, { id: id, role: "combobox", "aria-label": ariaLabel, "aria-expanded": isOpen, "aria-controls": navigable ? listboxId : undefined, "aria-haspopup": "listbox", "aria-autocomplete": "list", "aria-activedescendant": active >= 0 ? optionId(active) : undefined, "aria-describedby": describedBy, "aria-invalid": ariaInvalid || undefined, "aria-busy": loading || undefined, autoComplete: "off", value: query, placeholder: values.length > 0 && !props.multiple
                                            ? 'Заменить…'
                                            : atMax
                                                ? undefined
                                                : placeholder, disabled: disabled, onChange: (e) => handleInput(e.target.value), onFocus: () => setOpen(true), onMouseDown: () => setOpen(true), onKeyDown: handleKeyDown, className: "h-7 border-0 bg-transparent px-1 focus-visible:ring-0" }) }) }), _jsxs(Popover.Content, { align: "start", onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: (e) => {
                                    if (fieldRef.current?.contains(e.target))
                                        e.preventDefault();
                                }, 
                                // Список — в портале вне диалога: модальный Radix Dialog глушит
                                // колесо/тач-прокрутку снаружи себя (обработчик на document).
                                // Событие из списка до document не доходит — прокрутка работает.
                                onWheel: (e) => e.stopPropagation(), onTouchMove: (e) => e.stopPropagation(), className: "w-[var(--radix-popover-trigger-width)] min-w-72 p-1", children: [urlWithoutId ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-state-warning-fg)]", role: "alert", children: CAMPAIGN_URL_NO_ID_TEXT })) : null, error !== null && !urlWithoutId ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-state-danger-fg)]", role: "alert", children: error })) : null, showLoading ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-text-muted)]", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" })) : items.length === 0 ? (error === null && !urlWithoutId ? (_jsx("div", { className: "px-2 py-1.5 text-sm text-[var(--color-text-muted)]", children: emptyLabel })) : null) : (_jsx("ul", { id: listboxId, role: "listbox", "aria-label": ariaLabel, "aria-busy": loading || undefined, className: "max-h-72 overflow-y-auto", children: items.map((item, i) => (_jsx("li", { id: optionId(i), role: "option", "aria-selected": i === active, "data-current": selected.has(item.option.campaignId) || undefined, onMouseDown: (e) => e.preventDefault(), onMouseEnter: () => setHighlight(i), onClick: () => pick(item), className: cn('flex cursor-pointer items-center gap-2 rounded-[var(--radius-2)] px-2 py-1.5 text-sm', i === active
                                                ? 'bg-[var(--color-surface-sunken)] text-[var(--color-text-primary)]'
                                                : 'text-[var(--color-text-secondary)]', selected.has(item.option.campaignId) && 'font-medium'), children: item.kind === 'option' ? (_jsx(CampaignOptionRow, { option: item.option, showAccount: showAccount, note: item.note })) : (_jsxs("span", { className: "flex min-w-0 flex-1 items-center gap-2", children: [_jsx("span", { className: "shrink-0", children: "\u0412\u0437\u044F\u0442\u044C id" }), _jsx("span", { className: "shrink-0 font-mono text-xs tabular-nums", children: item.option.campaignId }), _jsxs("span", { className: "min-w-0 truncate text-xs text-[var(--color-text-muted)]", children: ["\u2014 ", rawIdHint] })] })) }, `${item.kind}:${item.option.campaignId}`))) }))] })] })] }), unresolved.length > 0 ? (_jsx("p", { id: hintId, className: "text-xs text-[var(--color-state-warning-fg)]", role: "status", "data-testid": "campaign-picker-unresolved", children: unresolved.length === 1
                    ? `Кампания ${unresolved[0]} ${rawIdHint}`
                    : `Кампании ${unresolved.join(', ')} ${rawIdHint.replace(/^не найдена/, 'не найдены')}` })) : null, foreignSelected.length > 0 ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "campaign-picker-foreign", children: foreignSelected.map(({ o, note }) => `«${o.name ?? o.campaignId}» — ${note}`).join('; ') })) : null, atMax && !disabled ? (_jsxs("p", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0411\u043E\u043B\u044C\u0448\u0435 \u0432\u044B\u0431\u0440\u0430\u0442\u044C \u043D\u0435\u043B\u044C\u0437\u044F \u2014 \u043F\u0440\u0435\u0434\u0435\u043B ", max] })) : null] }));
}
CampaignPicker.displayName = 'CampaignPicker';
//# sourceMappingURL=campaign-picker.js.map